import { type useUpgradeToProQuery } from '#app/__generated__/relay/useUpgradeToProQuery.graphql.js';
import { useModal } from '#app/components/Modals/V2/GlobalModalSystem/useModal.js';
import {
  type RedirectToStripeCheckoutType,
  useContraProStripeCheckout,
} from '#app/hooks/useContraProStripeCheckout.js';
import { useCurrentUser } from '#app/hooks/useCurrentUser.js';
import { useSelectedUserType } from '#app/hooks/useSelectedUserType.js';
import { graphql, useLazyLoadQuery } from '@contra/relay';
import { useCallback } from 'react';

type CTAType = 'UPGRADE_TO_PRO';

export const useUpgradeToPro = ({
  skip = false,
  successParameters,
}: {
  skip?: boolean;
  successParameters?: Parameters<RedirectToStripeCheckoutType>[0]['successParameters'];
} = {}): {
  hasActivePortfolioSubscription: boolean;
  hasPublishedPortfolio: boolean;
  initiateSubscriptionFlow: RedirectToStripeCheckoutType;
  isGeneratingStripeLink: boolean;
  refreshSubscription: () => Promise<void>;
  upgradeCTAType: CTAType | null;
} => {
  const { selectedUserType } = useSelectedUserType();
  const currentUser = useCurrentUser();
  const { isGeneratingStripeLink, refreshSubscription } =
    useContraProStripeCheckout();
  const data = useLazyLoadQuery<useUpgradeToProQuery>(
    graphql`
      query useUpgradeToProQuery($skip: Boolean!) {
        visitor @skip(if: $skip) {
          userAccount {
            profile {
              hasActivePortfolioSubscription
              hasPublishedPortfolio
            }
          }
        }
      }
    `,
    {
      skip: selectedUserType !== 'CONTRACTOR',
    },
  );

  const { openModal } = useModal({
    identifier: 'IndyPortfolioSubscriptionModal',
    modalProps: {
      initialBillingCycle: 'YEARLY',
      successModalConfiguration: {
        showSuccessModal: true,
      },
      successUrlParameters: successParameters,
    },
  });

  const hasActivePortfolioSubscription =
    data?.visitor?.userAccount?.profile?.hasActivePortfolioSubscription ??
    false;

  const hasPublishedPortfolio =
    data?.visitor?.userAccount?.profile?.hasPublishedPortfolio ?? false;

  const baseShouldShowCTA =
    !skip &&
    selectedUserType === 'CONTRACTOR' &&
    currentUser &&
    !hasActivePortfolioSubscription;

  const initiateSubscriptionFlow = useCallback(async () => {
    openModal();
  }, [openModal]);

  return {
    hasActivePortfolioSubscription,
    hasPublishedPortfolio,
    initiateSubscriptionFlow,
    isGeneratingStripeLink,
    refreshSubscription,
    upgradeCTAType: baseShouldShowCTA ? 'UPGRADE_TO_PRO' : null,
  };
};
